import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import type { GenericList } from './models/JobSharedInterfaces';
import { GetQuery, PostQuery } from './Shared';

interface AddPriceListTierPopupProps {
    systemCompanyId: number;
    showModal: boolean;
    hideModal(): void;
    saveModal(customerTierId: number, name: string, typeId: number): void;
}

interface TierDiscount {
    name: string;
    startDays: number;
    endDays: number;
    discountPercent: number;
}

const AddPriceListTier = (props: AddPriceListTierPopupProps) => {
    const [loading, setLoading] = React.useState(true);
    const [typeId, setTypeId] = React.useState(1);
    const [tierName, setTierName] = React.useState("");
    const [customerId, setCustomerId] = React.useState(-1);
    const [customerOptions, setCustomerOptions] = React.useState<GenericList[]>([]);
    const [tierDiscount, setTierDiscount] = React.useState<TierDiscount[]>([]);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});

    React.useEffect(() => {
        window.scrollTo(0, 0);
        if (props.showModal) {
            getData();
        }
    }, [props.showModal]);

    const getData = async () => {
        const apiUrl = "GetPriceListTierData?SystemCompanyId=" + props.systemCompanyId;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setCustomerOptions(result.data.customerOptions);
            setTierDiscount(result.data.tierDiscount);
            setLoading(false);
        }
    }

    const hideModal = () => {
        setTypeId(1);
        props.hideModal();
    }

    const clearErrors = (errorName: string) => {
        let errorList = errors;
        if (errorName) {
            delete errorList[errorName];
            setErrors(errorList);
        }
    }

    const onCellChange = (e: any, updatedIndex: number) => {
        setTierDiscount(prevState => prevState.map((item, index) =>
            index == updatedIndex ? { ...item, [e.target.name]: e.target.value } : item
        ));

        //if last row we need to add a new blank row
        let lastRow = tierDiscount.length - 1;
        if (updatedIndex === lastRow) {
            var blankRow = addBlankRow();
            setTierDiscount((prevItems) => [...prevItems, blankRow]);
        }
    }

    const addBlankRow = () => {
        var blankRow: TierDiscount = {
            name: "",
            startDays: 0,
            endDays: 0,
            discountPercent: 0
        };
        return blankRow;
    }

    const deleteItem = (e: any) => {
        e.preventDefault();

        setTierDiscount((state) => state.filter((item) => item.name !== e.target.value));
    }

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;

        if (typeId === 1 && !tierName) {
            errorList["tierName"] = "Tier Name is required!";
            setErrors(errorList);
            return false;
        }

        if (typeId === 2 && customerId <= 0) {
            errorList["customerId"] = "Customer is required!";
            setErrors(errorList);
            return false;
        }

        var discounts = tierDiscount;
        if (discounts.length > 0) {
            var errorString = "Please enter the following values: ";
            var error = false;
            //last row is a blank new row so ignore
            for (var i = 0; i < discounts.length - 1; i++) {
                var newErrorString = "";
                if (!discounts[i].name) {
                    newErrorString += " Name, ";
                    error = true;
                }
                var dayError = false;
                if (!discounts[i].startDays || discounts[i].startDays <= 0) {
                    newErrorString += " Start Day, ";
                    error = true;
                    dayError = true;
                }
                if (!discounts[i].endDays || discounts[i].endDays <= 0) {
                    newErrorString += " End Day, ";
                    error = true;
                    dayError = true;
                }
                if (!dayError && discounts[i].endDays <= discounts[i].startDays) {
                    newErrorString += " End Day > Start Day, ";
                    error = true;
                }
                if (!discounts[i].discountPercent || discounts[i].discountPercent <= 0) {
                    newErrorString += " Discount Percent, ";
                    error = true;
                }
                if (newErrorString != "") {
                    errorString += "\n"
                    errorString += "Row " + (i + 1) + ": " + newErrorString;
                    errorString = errorString.substring(0, errorString.length - 2);
                }
            }
            if (error) {
                formIsValid = false;
                errorList["discounts"] = errorString;
            }
        }

        setErrors(errorList);
        return formIsValid;
    }

    const saveModal = () => {
        if (validate()) {
            saveTierCustomer();
        } else {
            toast.error("Please fix validations errors before Saving");
        }
    }

    const saveTierCustomer = () => {
        if (typeId === 1) {
            saveTier();
        } else {
            saveCustomerTier();
        }
    }


    const saveTier = async () => {
        var discounts = [...tierDiscount];
        discounts.pop();

        let saveAddTier = {
            tierName: tierName,
            discounts: discounts
        };

        var jsonItem = JSON.stringify(saveAddTier);

        const apiUrl = "SavePriceListTier";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Price List Tier Saved Successfully");
                setTypeId(1);
                setTierName("");
                setCustomerId(-1);
                setTierDiscount([]);

                props.saveModal(result.data.tierId, result.data.tierName, 1);
            }
        } 
    }

    const saveCustomerTier = async () => {
        var customer = customerOptions.find(c => c.id === customerId);
        var discounts = [...tierDiscount];
        discounts.pop();

        let saveAddTier = {
            customerid: customerId,
            customerName: customer?.name,
            isEws: false,
            discounts: discounts
        };

        var jsonItem = JSON.stringify(saveAddTier);

        const apiUrl = "SavePriceListCustom";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Price List Custom Saved Successfully");
                setTypeId(1);
                setTierName("");
                setCustomerId(-1);
                setTierDiscount([]);

                props.saveModal(customerId, customer?.name ?? "", 2);
            }
        }
    }

    let lastRow = tierDiscount.length - 1;

    const renderDetails = (
        <Modal show={props.showModal} onHide={hideModal}>
            <Modal.Header className="modal__header--colour">
                <Modal.Title>Add New PriceList Tier</Modal.Title>
                <button type="button" className="close" onClick={hideModal}>&times;</button>
            </Modal.Header>
            <Modal.Body>
                <div>
                    <div className="input-group">
                        <label htmlFor="typeId" className="pricelist-add-type--width">Price List Type</label>
                        <input type="radio" className="marginRight10" id="tier" name="typeId" value={1} checked={typeId === 1} onChange={(e) => setTypeId(Number(e.target.value))}></input>
                        <label className="label" htmlFor="tier">Tier</label>
                        <input type="radio" className="marginRight10" id="custom" name="typeId" value={2} checked={typeId === 2} onChange={(e) => setTypeId(Number(e.target.value))}></input>
                        <label className="label" htmlFor="custom">Custom</label>
                    </div>

                    <div className={typeId === 1 ? "" : "hidden"}>
                        <label className="input-group" htmlFor="tierName">
                            <span className="label">Tier Name</span>
                            <input className="input" type="text" maxLength={30} id="tierName" name="tierName" value={tierName} onChange={(e) => { clearErrors("customerId"); setTierName(e.target.value) }}></input>
                        </label>
                        <span className="label errors">{errors["tierName"]}</span>
                    </div>
                    <div className={typeId === 1 ? "hidden" : ""}>
                        <label className="input-group" htmlFor="customer">
                            <span className="label">Customer </span>
                            <select id="customerId" name="customerId" value={customerId} onChange={(e) => { clearErrors("customerId"); setCustomerId(Number(e.target.value)) }}>
                                <option hidden defaultValue="-1"></option>
                                {customerOptions.map(cust =>
                                    <option key={cust.id} value={cust.id}>{cust.name}</option>
                                )};
                            </select>
                        </label>
                        <span className="label errors">{errors["customerId"]}</span>
                    </div>

                    <div className={props.systemCompanyId === 2 ? "overflowAuto" : "hidden"}>
                        <table className="table table__small tableColours">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Start Day</th>
                                    <th>End Day</th>
                                    <th>Discount %</th>
                                    <th></th>
                                </tr>
                            </thead>
                            {tierDiscount.map((item, index) =>
                                <tbody key={index}>
                                    <tr>
                                        <td className="table__text--align">
                                            <input type='text' maxLength={30} id="name" name="name" className="form-control" value={item.name} onChange={(e) => onCellChange(e, index)} />
                                        </td>
                                        <td className="table__text--align">
                                            <input type='number' min="0" step="1" className='form-control textalignright pricelist-input-sm' id="startDays" name="startDays" value={item.startDays} onChange={(e) => onCellChange(e, index)} />
                                        </td>
                                        <td className="table__text--align">
                                            <input type='number' min="0" step="1" className='form-control textalignright pricelist-input-sm' id="endDays" name="endDays" value={item.endDays} onChange={(e) => onCellChange(e, index)} />
                                        </td>
                                        <td className="table__text--align">
                                            <input type='number' min="0" step="1" className='form-control textalignright pricelist-input-sm' id="discountPercent" name="discountPercent" value={item.discountPercent} onChange={(e) => onCellChange(e, index)} />
                                        </td>
                                        <td className="table__text--align">
                                            <div className={index === lastRow ? "hidden" : "delete--tablecell"}>
                                                <a className="makeitred" href="#" onClick={(e) => deleteItem(e)}>
                                                    <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            )}
                        </table>
                        {errors["discounts"] ?
                            (errors["discounts"]).split("\n").map((item: any, key: any) => {
                                return <span className="label errors" key={key}>{item}<br /></span>
                            })
                            : ""}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button className="popup-button--yes" onClick={saveModal}>Save</button>
            </Modal.Footer>
        </Modal>

    )

    let contents = !props.showModal ? "" :
        loading ? <p><em>Loading...</em></p>
            : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default AddPriceListTier;